import type { Dictionnaire } from '../type';

export const sorties: Dictionnaire['sorties'] = {
  erreurs: {
    introuvable: 'Event not found',
    titre: 'Give your event a title',
    lieu: 'Enter a meeting place',
    adresse: 'Pick an address from the suggested list',
    horsZone: "Pick an address in this spot's country.",
    lien: "This link doesn't lead anywhere: check it, or leave the field empty",
    dateInvalide: 'Choose a valid date',
    datePassee: 'This date has already passed',
    heureDebut: 'Enter the start time',
    heureFin: 'Enter the end time',
    finDifferente: 'The end time must be different from the start time',
    passe: {
      inscrire: 'you can no longer join it',
      valider: 'requests can no longer be approved',
      modifier: 'it can no longer be edited',
      figee: 'its participant list is locked',
      retirer: 'you can no longer leave it',
      reprendre: 'there is nothing left to take over',
      supprimer: 'it can no longer be deleted',
    },
    annuleFaute: 'This event was canceled for lack of participants',
    complet: 'All the spots are taken, free one up before accepting',
    pasInscrite: "This person isn't signed up for this event",
    feuillePasFinie: 'Attendance is checked once the event is over',
    feuilleValidee: 'This attendance sheet has already been confirmed',
    note: 'A rating goes from 1 to 5',
    finApresDebut: 'The end time must come after the start time',
    tropTard: 'This event starts in less than ten hours: it can no longer be edited',
    creneauPasse: 'This time slot has already passed',
    organisateurReste: "You're organizing this event, you can't remove yourself",
    sansGroupe: "This event doesn't have a group",
    seulOrganisateur: 'Only the organizer manages participants',
    organisateurPart: "You're organizing this event: hand it over or delete it",
    autreQuelquun: 'Choose someone else to take over the event',
    neParticipePas: "This person isn't taking part in the event",
    nombreParticipants: (min: number, max: number) => `The number of participants must be between ${min} and ${max}`,
    suspendu: (jusqua: number) =>
    `You missed two events without letting anyone know: you can sign up again on ${new Date(jusqua).toLocaleDateString('en-GB')}`,
    estPasse: (raison: string) => `This event is over: ${raison}`,
    creneauPris: (titre: string) => `You're already busy at that time, with “${titre}”`,
  },
  carte: {
    inscrit: 'Signed up',
    enAttente: 'Pending',
    complet: 'Full',
    note: (note: string) => `Organizer rated ${note} out of 5`,
    participants: (n: number, max: number) => `${n}/${max} participants`,
  },
  commun: {
    autresActions: 'More actions',
    lieu: 'Place',
    date: 'Date',
    participants: 'Participants',
    monBillet: 'My ticket',
    enLigne: 'Online',
    masquerQ: 'Hide this event?',
    masquer: 'Hide this event',
    actionImpossible: "Can't do that right now.",
  },
  fiche: {
    noteNonEnvoyee: "This rating couldn't be sent.",
    groupeImpossible: "Couldn't open the group",
    ecrireImpossible: "You can't write to them right now.",
    desinscrireQ: 'Leave this event?',
    titre: 'Event',
    adresseComplete: 'Full address',
    partager: 'Share event',
    retirerMasques: 'Remove from hidden',
    presenceEnregistree: 'Your attendance was recorded.',
    montreLeCarre: 'Show this code to the organizer when you arrive: it confirms you were there.',
    passe: 'This event is over',
    gerer: 'Manage my event',
    rejoindreGroupe: 'Join the group',
    groupe: 'The event group',
    monAgenda: 'My calendar',
    presenceConfirmee: 'Attendance confirmed',
    horaire: 'Time',
    places: 'Spots',
    lien: 'Link',
    lienApresInscription: 'After signing up',
    rejoindre: (hote: string) => `Join on ${hote}`,
    noteOrganisateur: 'Rate the organizer',
    commentCaSEstPasse: 'How was this event?',
    noteSurCinq: 'Rating out of 5',
    langues: 'Languages',
    description: 'Description',
    organise: 'Organizer',
    prevenir: 'Let the organizer know if you can no longer come. Two no-shows close event sign-ups for a week.',
    autresApresAcceptation: 'The other participants appear once your participation is accepted.',
    annulerDemande: 'Cancel my request',
    meDesinscrire: 'Leave the event',
    listeAttente: 'Join the waiting list',
    demanderAParticiper: 'Request to join',
    participer: 'Join',
    gererDemandes: (n: number) => `Manage · ${n} request${n === 1 ? '' : 's'}`,
    suspendu: (jusqua: number) =>
    `Two no-shows: you can sign up again on ${new Date(jusqua).toLocaleDateString('en-GB')}`,
    organisePar: (nom: string) => `Organized by ${nom}`,
    ecrireA: (nom: string) => `Write to ${nom}`,
    noteAide: (nom: string) => `Rate ${nom}. A rating can't be taken back.`,
    surCinq: (n: number) => `${n} out of 5`,
    donnerQ: (n: number, nom: string) =>
    `Give ${nom} ${n} star${n === 1 ? '' : 's'}? This rating is final.`,
    demandesEnAttente: (n: number) => `${n} request${n === 1 ? '' : 's'} waiting for your answer.`,
  },
  gestion: {
    groupeImpossible: "Couldn't create the group.",
    passationImpossible: "Couldn't hand over right now.",
    feuilleTousLa: 'Attendance confirmed. Everyone was there.',
    validationImpossible: "Couldn't confirm right now.",
    scan: {
      pasSirocco: "This code isn't from Sirocco.",
      autreSortie: 'This ticket is for another event.',
      pasInscrite: "This person isn't signed up here.",
      impossible: "Couldn't record it.",
      dejaPresent: (nom: string) => `${nom} is already marked present.`,
      present: (nom: string) => `${nom} · present`,
    },
    suppressionImpossible: "Couldn't delete right now.",
    quitter: 'Leave the event',
    supprimerQ: 'Delete the event?',
    supprimer: 'Delete the event',
    aQui: 'Who are you handing the organization over to?',
    modifier: 'Edit event',
    scanner: 'Scan tickets',
    reserve: 'Organizer only',
    reserveAide: 'Only the person organizing this event can manage its participants.',
    tropTard: 'Too late to edit: the event starts in less than ten hours.',
    scannerUn: 'Scan a ticket',
    ouvrirGroupe: 'Open the group',
    creerGroupe: 'Create the group',
    personneAReunir: 'No one to bring together',
    demandes: 'Requests',
    feuilleAVerifier: 'Attendance sheet to check',
    feuilleAide: 'Open the participants, tick those who came, then confirm. The others will be counted absent; two no-shows close a member’s sign-ups for a week.',
    validerTousQ: 'Confirm attendance? Everyone is marked present.',
    valider: 'Confirm attendance',
    modificationImpossible: "Couldn't save the changes.",
    champs: {
      titre: 'Title',
      debut: 'Start',
      fin: 'End',
    },
    placesGardees: 'Participants already signed up keep their spot. The number of spots can’t be changed.',
    enregistrer: 'Save',
    ouvertureCamera: 'Opening the camera…',
    feuilleAbsents: (n: number) => `Attendance confirmed. ${n} no-show${n === 1 ? '' : 's'} recorded.`,
    laisserQ: (nom: string) => `Hand the organization over to ${nom}?`,
    valideeLe: (date: string) => `Attendance confirmed on ${date}`,
    validerQ: (n: number) =>
    `Confirm attendance? ${n} ${n === 1 ? 'person will be' : 'people will be'} counted absent.`,
  },
  categories: {
    sport: 'Sport',
    culture: 'Culture',
    pro: 'Pro',
    food: 'Food',
    asso: 'Society',
    etudes: 'Study',
    jeux: 'Games',
  },
  reserve: {
    titre: 'Organizer only',
  },
  creation: {
    impossible: "Couldn't publish",
    titreSortie: 'Suggest an outing',
    titreEvenement: 'Create an event',
    publierSortie: 'Post the outing',
    publierEvenement: 'Post the event',
    type: 'Outing type',
    evenement: 'Event',
    spontanee: 'Spontaneous',
    enLigne: 'Online',
    affiche: 'Poster (optional)',
    choisirAffiche: 'Choose a poster',
    retirerAffiche: 'Remove poster',
    quoi: 'What?',
    titre: 'Title',
    exempleSortie: 'Coffee at the Clock',
    exempleEvenement: '5-a-side football',
    categorie: 'Category',
    horaires: 'Time',
    heureDebut: 'Start time',
    heureFin: 'End time',
    ouSurLeCampus: 'Where on campus? Lecture hall, room, field…',
    campusAile: (aile: string) => (aile.length <= 2 ? `Wing ${aile}` : aile),
    campusEtage: (n: number) => (n === 0 ? 'ground floor' : n === 1 ? '1st floor' : n === 2 ? '2nd floor' : `floor ${n}`),
    chercheAdresse: 'Search for an address',
    lien: 'Joining link (optional)',
    lienExemple: 'https://zoom.us/j/…',
    lienAide: 'A video call, a Discord server… Only people who sign up will see it on the event page.',
    placeSurLePlan: 'Placed on the map, tap to move it',
    placerSurLePlanBouton: 'Place on the campus map',
    annuaireMuet: "The address directory isn't responding. Try again in a moment.",
    aucuneAdresse: 'No address found. Add the street or the name of the place.',
    nombreParticipants: 'Number of participants',
    maximum: 'Maximum',
    retirerPlace: 'Remove a spot',
    ajouterPlace: 'Add a spot',
    seConfirme: 'Confirmed from',
    abaisserSeuil: 'Lower the minimum',
    aucun: 'None',
    releverSeuil: 'Raise the minimum',
    description: 'Description',
    descriptionExemple: 'What people should know: level, gear, meeting point...',
    validation: 'Approve participants',
    langues: 'Spoken language',
    languesTitre: 'Spoken languages',
    languesMax: 'Three languages at most.',
    languesChercher: 'Search for a language',
    languesAucune: 'No language matches.',
    languesTermine: 'Done',
    languesNoms: { ary: 'Moroccan Arabic', zgh: 'Amazigh' },
  },
  filtres: {
    titre: 'Event filters',
    quand: 'When',
    du: 'From',
    aPartirDu: 'From',
    au: 'to',
    jusquAu: 'Until',
    distance: 'Distance',
    rayon: 'Radius around me',
    sansDistance: 'No distance set',
    sansPosition: 'Location not found. The distance filter needs your location.',
    nature: 'Type',
    spontanees: 'Spontaneous outings',
    enLigne: 'Online',
    categories: 'Categories',
    langues: 'Languages',
    toutesLangues: 'All languages',
    appliquer: 'Apply filters',
    effacer: 'Clear',
    kilometres: (n: number) => `${n} kilometer${n === 1 ? '' : 's'}`,
  },
  liste: {
    rechercher: 'Search...',
    carte: 'Events map',
    agenda: 'My calendar',
    creer: 'Create an event',
    masques: 'Hidden events',
    filtreActif: 'Filter · a filter is on',
    filtrer: 'Filter',
    unSansAdresse: "1 outing without a precise address isn't counted in this radius.",
    aucun: 'No events',
    elargir: 'Try widening your categories.',
    personne: 'No one has suggested an outing here yet.',
    proposer: 'Suggest an outing',
    pourToi: 'For you',
    toutes: 'All outings',
    sansAdresse: (n: number) => `${n} outings without a precise address aren't counted in this radius.`,
    rienAMoinsDe: (km: number) => `Nothing within ${km} km of you. Try a wider radius.`,
  },
  agenda: {
    onglets: {
      aVenir: 'Upcoming',
      jOrganise: "I'm organizing",
    },
    titre: 'My calendar',
    semaine: 'THIS WEEK',
    plusTard: 'LATER',
    rien: 'Nothing to show here',
    rienAide: 'Sign up for an outing or suggest your own: it will appear here.',
    rienOrganise: 'The events you organize will appear here.',
    voir: 'See events',
    proposer: 'Suggest an outing',
  },
  participants: {
    titre: 'Participants',
    reserveAide: 'Only the person organizing this event can see this list.',
    chercher: 'Search for a participant',
    personne: 'No one by that name in this event.',
    present: 'Present',
    organise: 'Organizer',
    absent: 'Absent',
    retirerQ: (nom: string) => `Remove ${nom} from the event?`,
    retirerPresence: (nom: string) => `Remove ${nom}'s attendance`,
    estLa: (nom: string) => `${nom} is here`,
  },
  demandes: {
    titre: 'Requests',
    reserveAide: 'Only the person organizing this event can handle its requests.',
    chercher: 'Search requests',
    personne: 'No one by that name in the requests.',
    aucune: 'No requests',
    aucuneAide: 'Participation requests will arrive here.',
    ouvert: 'This event accepts everyone automatically, as long as there are spots left.',
    rang: (rang: number) => `#${rang} on the list`,
    accepter: (nom: string) => `Accept ${nom}`,
    refuser: (nom: string) => `Decline ${nom}`,
  },
  miennes: {
    onglets: {
      inscrit: 'Signed up',
      organise: 'Organized',
      passes: 'Past',
    },
    vides: {
      inscrit: 'Your sign-ups will appear here.',
      organise: 'The events you organize will appear here.',
      passes: 'Events where your attendance was confirmed will appear here.',
    },
    titre: 'My events and outings',
    uneFeuille: 'One attendance sheet to check',
    rien: 'Nothing to show here',
    feuilles: (n: number) => `${n} attendance sheets to check`,
  },
  masques: {
    titre: 'Hidden events',
    aucun: 'No hidden events',
    aucunAide: 'The ones you hide from their page end up here.',
    remettre: 'Open an event to put it back in your lists.',
  },
};
